import React from 'react'
import { ChevronDown } from 'lucide-react'
import { Button, Modal } from '@/components'
import type { useUserManagementViewModel } from '../useUserManagementViewModel'

type VM = ReturnType<typeof useUserManagementViewModel>

interface AddUserModalProps {
  showAddModal: VM['showAddModal']
  onClose: () => void
  showRoleDropdown: VM['showRoleDropdown']
  setShowRoleDropdown: VM['setShowRoleDropdown']
  showCompanyDropdown: VM['showCompanyDropdown']
  setShowCompanyDropdown: VM['setShowCompanyDropdown']
  newUser: VM['newUser']
  setNewUser: VM['setNewUser']
  companies: VM['companies']
  handleAddUser: VM['handleAddUser']
  /** Modal ici hata slotu: validasyon + sunucu hatasi (backend mesaji dahil). */
  error: string | null
  onDismissError: () => void
  isSubmitting: boolean
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  showAddModal,
  onClose,
  showRoleDropdown,
  setShowRoleDropdown,
  showCompanyDropdown,
  setShowCompanyDropdown,
  newUser,
  setNewUser,
  companies,
  handleAddUser,
  error,
  onDismissError,
  isSubmitting,
}) => {
  return (
    <Modal
      isOpen={showAddModal}
      onClose={onClose}
      title="Yeni Kullanıcı Ekle"
      error={error}
      onDismissError={onDismissError}
      isSubmitting={isSubmitting}
      size="sm"
      footer={
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            İptal
          </Button>
          <Button
            onClick={handleAddUser}
            loading={isSubmitting}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Kullanıcı Ekle
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ad *
            </label>
            <input
              type="text"
              value={newUser.first_name}
              onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              placeholder="Ad"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Soyad *
            </label>
            <input
              type="text"
              value={newUser.last_name}
              onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              placeholder="Soyad"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            E-posta *
          </label>
          <input
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            placeholder="E-posta adresi"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Şifre *
          </label>
          <input
            type="text"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            disabled={isSubmitting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            placeholder="Şifre (en az 6 karakter)"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rol *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowRoleDropdown(!showRoleDropdown)
                setShowCompanyDropdown(false)
              }}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between disabled:bg-gray-100"
            >
              <span>
                {newUser.role === 'admin' ? 'Yönetici' :
                 newUser.role === 'superadmin' ? 'Süper Yönetici' :
                 newUser.role === 'customer' ? 'Müşteri' : 'Operatör'}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showRoleDropdown && (
              <div className="absolute z-[999999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setNewUser({ ...newUser, role: 'operator' })
                    setShowRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-lg"
                >
                  Operatör
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewUser({ ...newUser, role: 'admin' })
                    setShowRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                >
                  Yönetici
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewUser({ ...newUser, role: 'customer' })
                    setShowRoleDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 last:rounded-b-lg"
                >
                  Müşteri
                </button>

              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Şirket *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setShowCompanyDropdown(!showCompanyDropdown)
                setShowRoleDropdown(false)
              }}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between disabled:bg-gray-100"
            >
              <span>
                {newUser.company_id === 0
                  ? 'Şirket Seçin'
                  : companies.find(c => c.id === newUser.company_id)?.name || 'Şirket Seçin'
                }
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showCompanyDropdown && (
              <div className="absolute z-[999999] w-full bottom-full mb-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    setNewUser({ ...newUser, company_id: 0 })
                    setShowCompanyDropdown(false)
                  }}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 first:rounded-t-lg text-gray-500"
                >
                  Şirket Seçin
                </button>
                {companies.map((company) => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => {
                      setNewUser({ ...newUser, company_id: company.id })
                      setShowCompanyDropdown(false)
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 last:rounded-b-lg"
                  >
                    {company.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddUserModal
